<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTypedStore } from '@/store';
import { useTablePaginator } from '@/composables/useTableLS';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useApi } from '@/api';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useLs } from '@/setup/vueLs';
import _ from '@/setup/lodash';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import type { UiTableNewField } from '@/types/table';
//#endregion

//#region types
type HistoryTableRow = {
  date: string,
  affiliates_count: number,
  payments_count: number,
  paid_amount: number,
};

type OutsidersTableRow = {
  affiliate_id: string,
  affiliate_email: string,
  latest_payment_amount: number,
  latest_payment_at: string,
  last_7_days_revenue: number,
  last_14_days_revenue: number,
  last_30_days_revenue: number,
};
//#endregion

//#region const
const LS_PATH = 'Payments/Dashboard';

const sizes = {
  1080: 20,
  1440: 30,
};

const historyTableFields: UiTableNewField<HistoryTableRow>[] = [
  {
    name: 'date',
    align: 'left',
    headerAlign: 'left',
    class: 'link',
    format: 'format-date',
    link: dateClickHandler,
  },
  {
    name: 'affiliates_count',
    align: 'right',
    headerAlign: 'left',
    width: '100',
  },
  {
    name: 'payments_count',
    align: 'right',
    headerAlign: 'left',
    width: '100',
  },
  {
    name: 'paid_amount',
    align: 'right',
    headerAlign: 'left',
    width: '200',
    class: 'bold',
    format: 'formatMoney',
  },
];

const outsidersTableFields: UiTableNewField<OutsidersTableRow>[] = [
  {
    name: 'affiliate_email',
    align: 'left',
    class: 'link',
    headerAlign: 'left',
    link: affiliateClickHandler,
  },
  {
    name: 'latest_payment_amount',
    align: 'right',
    headerAlign: 'left',
    width: '80',
    class: 'bold',
    format: 'formatMoney',
    computedClass: getCurrencyClass,
  },
  {
    name: 'latest_payment_at',
    align: 'left',
    headerAlign: 'left',
    width: '120',
    format: 'date-time',
  },
  {
    name: 'last_7_days_revenue',
    align: 'right',
    headerAlign: 'left',
    width: '100',
    format: 'formatMoney',
    computedClass: getCurrencyClass,
  },
  {
    name: 'last_14_days_revenue',
    tip: '14 Days',
    align: 'right',
    headerAlign: 'left',
    width: '100',
    format: 'formatMoney',
    computedClass: getCurrencyClass,
  },
  {
    name: 'last_30_days_revenue',
    align: 'right',
    headerAlign: 'left',
    width: '100',
    format: 'formatMoney',
    computedClass: getCurrencyClass,
  },
];
//#endregion

//#region composables
const api = useApi();
const ls = useLs();
const route = useRoute();
const store = useTypedStore();
const { checkRequest, checkRoute } = useDetectPermissions();
const { checkCurrencyCode } = useFormatCurrency();
const updateUrl = useUpdateUrl();

const { limit, count, page } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
//#endregion

//#region state
const historyTable = ref({
  data: [] as HistoryTableRow[],
  currencyCode: '',
  page: 1,
  limit: 0,
  count: 0,
  isDataLoading: false,
  isFirstLoading: true,
  totals: {},
});

const outsidersTable = ref({
  data: [] as OutsidersTableRow[],
  currencyCode: '',
  page: 1,
  limit: 0,
  count: 0,
  isDataLoading: false,
  isFirstLoading: true,
});
//#endregion

//#region computed
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
//#endregion

//#region methods
function initData() {
  if (checkRequest('payments_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    historyTable.value.limit = Number(_.get(_urlData, ['tableHistory', 'limit'])) || limit.value;
    outsidersTable.value.limit = Number(_.get(_urlData, ['tableOutsiders', 'limit'])) || limit.value;

    historyTable.value.page = Number(_.get(_urlData, ['tableHistory', 'page'])) || 1;
    outsidersTable.value.page = Number(_.get(_urlData, ['tableOutsiders', 'page'])) || 1;

    outsidersTable.value.currencyCode = checkCurrencyCode(_.get(_urlData, ['tableOutsiders', 'currencyCode'], ls.get(`${LS_PATH}/outsiders_currency_code`)));
    historyTable.value.currencyCode = checkCurrencyCode(_.get(_urlData, ['tableHistory', 'currencyCode'], ls.get(`${LS_PATH}/history_currency_code`)));

    nextTick(() => {
      getSummaryReport();
      getOutsidersReport();
    });
  }

  checkRoute('payments_view');
}

function changeHistoryCurrency(value: string) {
  historyTable.value.currencyCode = value;
  ls.set(`${LS_PATH}/history_currency_code`, value);
  getSummaryReport(value);
}

function changeOutsidersCurrency(value: string) {
  outsidersTable.value.currencyCode = value;
  ls.set(`${LS_PATH}/outsiders_currency_code`, value);
  getOutsidersReport(value);
}

const _query = (table: any) => {
  return {
    limit: table.limit,
    offset: table.limit * table.page - table.limit,
    currency_code: table.currencyCode,
  };
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    tableHistory: {
      limit: historyTable.value.limit,
      page: historyTable.value.page,
      currencyCode: historyTable.value.currencyCode,
    },
    tableOutsiders: {
      limit: outsidersTable.value.limit,
      page: outsidersTable.value.page,
      currencyCode: outsidersTable.value.currencyCode,
    },
  };

  updateUrl.updateGetParams(_dataFilters);
};

async function getSummaryReport(currencyCode?: string) {
  if (!historyTable.value.isFirstLoading) {
    historyTable.value.isDataLoading = true;
  }

  if (currencyCode) {
    historyTable.value.currencyCode = currencyCode;
  }

  _completedFilterUrl();
  let options = _query(historyTable.value);

  try {
    const { data: { payload, misc } } = await api.getSummaryReport(options);

    historyTable.value.count = misc!.count;
    //@ts-expect-error в api нет значения currency_code
    if (misc?.currency_code) {
      //@ts-expect-error в api нет значения currency_code
      historyTable.value.currencyCode = misc?.currency_code;
    }
    historyTable.value.data = _.cloneDeep(payload.data);
    historyTable.value.totals = _.clone(payload.totals);
  } finally {
    if(!historyTable.value.isFirstLoading) {
      historyTable.value.isDataLoading = false;
    } else {
      historyTable.value.isFirstLoading = false;
    }
  }
}

async function getOutsidersReport(currencyCode?: string) {
  if (!outsidersTable.value.isFirstLoading) {
    outsidersTable.value.isDataLoading = true;
  }

  if (currencyCode) {
    outsidersTable.value.currencyCode = currencyCode;
  }

  _completedFilterUrl();
  let options = _query(outsidersTable.value);

  try {
    const { data: { payload, misc } } = await api.getOutsidersReport(options);

    outsidersTable.value.count = misc!.count;
    //@ts-expect-error в api нет значения currency_code
    if (misc?.currency_code) {
      //@ts-expect-error в api нет значения currency_code
      outsidersTable.value.currencyCode = misc?.currency_code;
    }
    outsidersTable.value.data = _.cloneDeep(payload.data);
  } finally {
    if(!outsidersTable.value.isFirstLoading) {
      outsidersTable.value.isDataLoading = false;
    } else {
      outsidersTable.value.isFirstLoading = false;
    }
  }
}

const historyPageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (historyTable.value.page !== pageVal || historyTable.value.limit !== size) {
    historyTable.value.page = pageVal;
    historyTable.value.limit = size;
    getSummaryReport();
  }
};
const outsidersPageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (outsidersTable.value.page !== pageVal || outsidersTable.value.limit !== size) {
    outsidersTable.value.page = pageVal;
    outsidersTable.value.limit = size;
    getOutsidersReport();
  }
};

function dateClickHandler(item: HistoryTableRow) {
  return `/payments/payout/paid?from=payment_dashboard&date=${item.date}`;
}

function affiliateClickHandler(item: OutsidersTableRow) {
  return `/affiliates/${item.affiliate_id}/info`;
}

function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="payments-dashboard">
    <div class="payments-dashboard__column_left">
      <UiToolbar>
        <template v-slot:title>
          {{ $t('payments.dashboard.title') }}
        </template>
        <template v-slot:right-slot>
          <UiCurrencyPicker
            :model-value="historyTable.currencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeHistoryCurrency"
          />
          <UiPagination
            :page="historyTable.page"
            :page-size="historyTable.limit"
            :count="historyTable.count"
            @page-change="historyPageChange"
          />
        </template>
      </UiToolbar>
      <UiTableNew
        v-loading="historyTable.isDataLoading"
        :fields="historyTableFields"
        :data="historyTable.data"
        :rows-count="historyTable.limit"
        :total="historyTable.totals"
        :currency="historyTable.currencyCode"
        lazy-loading
        show-total
        i18n-path="payments.dashboard"
        disable-sort
        is-static
      />
    </div>
    <div class="payments-dashboard__column_right">
      <UiToolbar>
        <template v-slot:title>
          {{ $t('payments.outsiders.title') }}
        </template>
        <template v-slot:right-slot>
          <UiCurrencyPicker
            :model-value="outsidersTable.currencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeOutsidersCurrency"
          />
          <UiPagination
            :page="outsidersTable.page"
            :page-size="outsidersTable.limit"
            :count="outsidersTable.count"
            @page-change="outsidersPageChange"
          />
        </template>
      </UiToolbar>
      <UiTableNew
        v-loading="outsidersTable.isDataLoading"
        :fields="outsidersTableFields"
        :data="outsidersTable.data"
        :rows-count="outsidersTable.limit"
        :currency="outsidersTable.currencyCode"
        lazy-loading
        i18n-path="payments.outsiders"
        disable-sort
        is-static
      />
    </div>
  </div>
</template>

<style lang="scss">
  .payments-dashboard {
    display: flex;
    gap: 32px;
    &__column {
      &_left {
        width: 43%;
        min-width: 460px;
      }
      &_right {
        width: 57%;
        min-width: 620px;
      }
    }
  }
</style>
